import { useLayoutEffect, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, ContactShadows } from '@react-three/drei';
import { Truck } from './Truck';
import { RoomEnv } from './truck/RoomEnv';
import { FitCamera } from './FitCamera';
import { EveningStage } from './EveningStage';
import { MoodSwitch } from './MoodSwitch';
import { useTruckMaterials } from './truck/materials';
import { TRUCK_Y } from './truck/layout';
import { useConfig } from '../store';
import { useReducedMotion } from '../motion';

const BAKE_SECONDS = 1.5;
const BAKE_MIN_FRAMES = 3;

function ShadowBake({ bakeKey, settled, onSettled }: { bakeKey: string; settled: boolean; onSettled: (key: string) => void }) {
  const gl = useThree((s) => s.gl);
  const invalidate = useThree((s) => s.invalidate);
  const left = useRef({ key: '', seconds: 0, frames: 0 });

  useLayoutEffect(() => {
    gl.shadowMap.autoUpdate = false;
    return () => {
      gl.shadowMap.autoUpdate = true;
    };
  }, [gl]);

  useFrame((_, dt) => {
    const l = left.current;
    if (l.key !== bakeKey) {
      l.key = bakeKey;
      l.seconds = BAKE_SECONDS;
      l.frames = BAKE_MIN_FRAMES;
    }
    if (settled) return;
    gl.shadowMap.needsUpdate = true;
    l.seconds -= Math.min(dt, 0.1);
    l.frames -= 1;
    if (l.seconds <= 0 && l.frames <= 0) onSettled(bakeKey);
    else invalidate();
  });

  return null;
}

function TruckStage() {
  const mats = useTruckMaterials();
  return (
    <>
      <FitCamera margin={1.08}>
        <Truck mats={mats} />
      </FitCamera>
      <EveningStage mats={mats} />
    </>
  );
}

export function TruckScene() {
  const bakeKey = useConfig((s) => `${s.showAwning}-${s.showBar}-${s.showLights}-${s.showSignage}`);
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const settled = settledKey === bakeKey;
  const reducedMotion = useReducedMotion();

  return (
    <div className="relative h-full w-full overflow-hidden bg-[#061936]">
      <Canvas frameloop="demand" shadows="percentage" dpr={[1, 2]} camera={{ position: [5.2, 1.15, 4.7], fov: 34 }} gl={{ antialias: true }}>
        <color attach="background" args={['#061936']} />

        <RoomEnv />
        <TruckStage />

        <ShadowBake bakeKey={bakeKey} settled={settled} onSettled={setSettledKey} />
        <ContactShadows
          key={settled ? `baked-${bakeKey}` : 'live'}
          frames={settled ? 1 : Infinity}
          position={[0, TRUCK_Y, 0]}
          opacity={0.55}
          scale={8}
          blur={2.4}
          far={3}
          resolution={1024}
        />
        <OrbitControls makeDefault autoRotate={!reducedMotion} autoRotateSpeed={0.6} enablePan={false} enableDamping={!reducedMotion} maxPolarAngle={Math.PI * 0.49} />
      </Canvas>

      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_95%_at_50%_46%,rgb(6_25_54/0)_58%,rgb(3_11_26/0.42)_100%)]" />

      <p className="pointer-events-none absolute bottom-0 left-0 px-4 py-3 font-mono text-[11px] uppercase tracking-widest text-white/60 wide:top-0 wide:bottom-auto wide:p-4">
        <span className="pointer-coarse:hidden">Drag to orbit · scroll to zoom</span>
        <span className="hidden pointer-coarse:inline">Drag to orbit · pinch to zoom</span>
      </p>

      <MoodSwitch />
    </div>
  );
}
